<script setup lang="ts">
/**
 * UiModal — обёртка для модалок (inbox-134).
 *
 * Slots:
 *   header  — кастомный header (override title)
 *   default — body content (form, etc.)
 *   footer  — actions row (cancel/submit buttons)
 *
 * Не управляет open/close — это делает useModalStore (ModalRoot.vue).
 * Эмитит `close` на крестик / @click.self по overlay'у.
 *
 * inbox-459 — встроен focus trap через useFocusTrap:
 *   - autofocus первого focusable элемента;
 *   - Esc → emit('close') если !disableClose;
 *   - Tab/Shift+Tab циклит внутри modal-card;
 *   - при закрытии возвращает фокус на предыдущий элемент.
 *
 * CSS port из docs/mockups/modal-create-link.html (.modal-overlay /
 * .modal-card / .modal-head / .modal-body / .modal-foot).
 *
 * Usage:
 *   <UiModal title="Новая ссылка" size="md" @close="modals.close">
 *     <form>…</form>
 *     <template #footer>
 *       <UiButton variant="ghost" @click="modals.close">Отмена</UiButton>
 *       <UiButton type="submit">Создать</UiButton>
 *     </template>
 *   </UiModal>
 */

import { ref } from 'vue';
import { useFocusTrap } from '@/composables/useFocusTrap';

interface Props {
  title?: string;
  subtitle?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Запрещает закрытие (например, во время submit). */
  disableClose?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  subtitle: '',
  size: 'md',
  disableClose: false,
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

function onClose() {
  emit('close');
}

const cardRef = ref<HTMLElement | null>(null);
useFocusTrap(cardRef, {
  onEscape: () => {
    if (!props.disableClose) emit('close');
  },
});
</script>

<template>
  <div
    class="modal-overlay"
    role="presentation"
    @click.self="!disableClose && onClose()"
  >
    <div
      ref="cardRef"
      class="modal-card"
      :data-size="size"
      role="dialog"
      aria-modal="true"
      :aria-label="title || undefined"
    >
      <header class="modal-head">
        <div class="modal-titles">
          <slot name="header">
            <h2 v-if="title" class="modal-title">{{ title }}</h2>
            <p v-if="subtitle" class="modal-sub">{{ subtitle }}</p>
          </slot>
        </div>
        <button
          type="button"
          class="modal-close"
          :disabled="disableClose"
          aria-label="Закрыть"
          @click="onClose"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            aria-hidden="true"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
      </header>

      <div class="modal-body">
        <slot />
      </div>

      <footer v-if="$slots.footer" class="modal-foot">
        <slot name="footer" />
      </footer>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(10, 13, 18, 0.7);
  backdrop-filter: blur(4px);
  display: grid;
  place-items: center;
  padding: 16px;
}

.modal-card {
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-radius: 14px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45);
}

.modal-card[data-size='sm'] { max-width: 480px; }
.modal-card[data-size='md'] { max-width: 720px; }
.modal-card[data-size='lg'] { max-width: 960px; }
.modal-card[data-size='xl'] { max-width: 1100px; }

.modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 24px 14px;
  border-bottom: 1px solid var(--border);
}

.modal-titles {
  flex: 1;
  min-width: 0;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0;
  color: var(--fg);
}

.modal-sub {
  font-size: 13px;
  color: var(--fg-muted);
  margin: 4px 0 0;
}

/* inbox-330 — 36×36 icon-button standard, r=6 (chip radius). */
.modal-close {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 6px;
  color: var(--fg-muted);
  background: transparent;
  border: 0;
  cursor: pointer;
  flex-shrink: 0;
}

.modal-close:hover:not(:disabled) {
  background: var(--bg-hover);
  color: var(--fg);
}

.modal-close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-body {
  padding: 18px 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border-top: 1px solid var(--border);
  background: var(--bg);
  justify-content: flex-end;
}

@media (max-width: 760px) {
  .modal-overlay {
    place-items: end stretch;
    padding: 0;
  }
  .modal-card {
    max-width: 100%;
    max-height: 92vh;
    border-radius: 14px 14px 0 0;
  }
  .modal-head { padding: 14px 18px 12px; }
  .modal-body { padding: 14px 18px; }
  .modal-foot { padding: 12px 18px; flex-wrap: wrap; }
  .modal-title { font-size: 16px; }
}
</style>
